import { useCallback, useEffect, useRef } from 'react'

import { Grid, Box, CircularProgress, Typography } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import { ControlledNumericFormat } from 'components/common'
import { DEBOUNCE_DELAY, Mode } from 'constants/index'
import { usePredictAndSetConsignmentFeeMutation } from 'generated/graphql'
import { Restricted } from 'providers'
import { TBookingForm } from 'types'

import { DynamicPricingVoteSection } from '../DynamicPricingVoteSection'

interface IDynamicPricingProps {
  mode?: number
  bookingId: number
}

export const DynamicPricing = (_props: IDynamicPricingProps) => {
  const { mode, bookingId } = _props
  const isInitialMount = useRef(true)

  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<TBookingForm>()

  const [
    distance,
    tariffId,
    collectionAddressBy,
    collectionAddressCity,
    collectionAddressPostcode,
  ] = useWatch({
    name: [
      'distance',
      'tariffId',
      'collectionAddresses.[0].by',
      'collectionAddresses.[0].city',
      'collectionAddresses.[0].postcode',
    ],
  })

  const [distanceValueDebounced] = useDebounce(distance, DEBOUNCE_DELAY)
  const formatedDistanceValueDebounced = Number(distanceValueDebounced)

  const hasRequiredFields = Boolean(
    bookingId &&
      formatedDistanceValueDebounced &&
      tariffId &&
      collectionAddressBy &&
      collectionAddressCity &&
      collectionAddressPostcode,
  )

  // Mutations
  const [predictAndSetConsignmentFee, { loading, data }] = usePredictAndSetConsignmentFeeMutation({
    onCompleted: (data) => {
      if (data.predictAndSetConsignmentFee?.consignmentFee) {
        setValue(
          'consignmentFeeFromDynamicPricing',
          data.predictAndSetConsignmentFee.consignmentFee,
          { shouldDirty: false },
        )
      }
    },
  })

  const predictAndSetPricing = useCallback(() => {
    predictAndSetConsignmentFee({
      variables: {
        input: {
          bookingId,
          distance: formatedDistanceValueDebounced,
          tariffId: Number(tariffId),
          collectionAddressBy,
          collectionAddressCity,
          collectionAddressPostcode,
        },
      },
    })
  }, [
    predictAndSetConsignmentFee,
    bookingId,
    formatedDistanceValueDebounced,
    tariffId,
    collectionAddressBy,
    collectionAddressCity,
    collectionAddressPostcode,
  ])

  // Subscriptions
  useEffect(() => {
    if (!hasRequiredFields) {
      return
    }

    const currentValue = getValues('consignmentFeeFromDynamicPricing')

    if (isInitialMount.current) {
      if (!currentValue) {
        predictAndSetPricing()
      }
      isInitialMount.current = false
      return
    }

    predictAndSetPricing()
  }, [
    bookingId,
    formatedDistanceValueDebounced,
    tariffId,
    collectionAddressBy,
    collectionAddressCity,
    collectionAddressPostcode,
    hasRequiredFields,
    getValues,
    predictAndSetPricing,
  ])

  return (
    <>
      <Grid item xs={12}>
        <ControlledNumericFormat
          control={control}
          name='consignmentFeeFromDynamicPricing'
          label='Consignment Fee from Dynamic Pricing'
          disabled={loading}
          InputProps={{
            readOnly: true,
          }}
          error={!!errors.consignmentFeeFromDynamicPricing}
          helperText={errors.consignmentFeeFromDynamicPricing?.message}
        />
      </Grid>

      {loading && (
        <Grid item xs={12}>
          <Box display='flex' alignItems='center'>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant='body2'>Calculating Dynamic Fee...</Typography>
          </Box>
        </Grid>
      )}

      <Restricted to='ViewDynamicPricingVote'>
        <Grid item xs={12}>
          {mode === Mode.Update &&
            Number(data?.predictAndSetConsignmentFee?.consignmentFee) > 0 && (
              <DynamicPricingVoteSection bookingId={bookingId} />
            )}
        </Grid>
      </Restricted>
    </>
  )
}
